import React, { useCallback, useEffect, useState } from 'react';
import { Spinner } from '@myob/myob-widgets';
import { Redirect } from 'react-router-dom';
import { useHistory } from 'react-router';
import Cookies from 'js-cookie';
import { useAuth0 } from 'hooks/Auth0/Auth0';
import { useAddPaymentInfoEvent } from 'hooks/GTMAnalytics/useGTMAnalytics';
import { useSendEventToSegment } from 'hooks/useSendEventToSegment/useSendEventToSegment';
import { useGetEcommercePrice } from 'hooks/GTMAnalytics/useGTMAnalyticsHelper';
import {
  trackEvent,
  useAddPaymentInfoEventGA4,
  usePartnerSignupPaymentInfoEventGA4,
  useQuoteAcceptPaymentInfoEventGA4
} from 'hooks/GTM4Analytics/useGTMAnalytics';
import { PaymentForm } from 'components/paymentForm/PaymentForm';
import { useSessionContext } from 'contexts/sessionProvider/SessionProvider';
import { useGetRecaptchaToken } from 'hooks/useRecaptcha/useRecaptcha';
import { useGetNextPageUrl } from 'hooks/useGetNextPage/useGetNextPageUrl';
import { DirectDebitForm } from './directDebitForm/directDebitForm';
import { CreditCardForm } from './credtiCardForm/creditCardForm';

import {
  COOKIE_AJS_ANONYMOUS_ID,
  COOKIE_AJS_USER_ID,
  COOKIE_MYOB_VISITOR_ID,
  FLOW_STEPS,
  GTM_ANALYTICS_CONSTANTS,
  PAYMENT_METHOD
} from '../../constants';
import { CreditCardDetail, DirectDebitDetail } from './types';
import '../../app/App.scss';
import './PaymentPage.scss';
import StripePaymentForm from './stripePaymentForm/StripePaymentForm';

export const PaymentPage: React.FC = () => {
  const {
    region,
    theme,
    promoCode
  } = useSessionContext();

  const { isLoading: isAuth0Loading, isAuthenticated } = useAuth0();

  const initialCardDetail = {
    cardType: '',
    cvv: '',
    expiryMonth: '',
    expiryYear: '',
    maskedNumber: '',
    nameOnCard: '',
    number: ''
  };

  const [creditCardDetail, setCreditCardDetail] = useState<CreditCardDetail>(initialCardDetail);

  const initialDirectDebitDetail: DirectDebitDetail = {
    accountName: '',
    authorised: false,
    bsb: '',
    accountNumber: '',
    bank: '',
    branch: '',
    account: '',
    suffix: ''
  };
  const [directDebitDetail, setDirectDebitDetail] = useState(initialDirectDebitDetail);
  const [paymentMethod, setPaymentMethod] = useState(PAYMENT_METHOD.CREDIT);
  const [paymentMethodId, setPaymentMethodId] = useState<string>('');

  const history = useHistory();
  const {
    hasPurchaseSubmitted,
    setPaymentMethod: setPaymentMethodInContext,
    isAnnualPrice,
    useLegacy
  } = useSessionContext();
  const { currency, totalPrice } = useGetEcommercePrice();

  // supporting the display of pci card portal for partner referrals and strategic partners
  const doNewFlow = !useLegacy;

  const myobVisitorId = Cookies.get(COOKIE_MYOB_VISITOR_ID);
  const anonymousId = Cookies.get(COOKIE_AJS_ANONYMOUS_ID);
  const userId = Cookies.get(COOKIE_AJS_USER_ID);
  const [submitting, setSubmitting] = useState(false);
  const { recaptchaToken } = useGetRecaptchaToken(submitting, 'submit');
  const isStripePaymentFormEnabled = doNewFlow;
  const confirmationPath = useGetNextPageUrl(FLOW_STEPS.PAYMENT);

  const doCheckout = () => {
    /* istanbul ignore next */
    const paymentProfile = isStripePaymentFormEnabled ? {
      paymentMethodId,
      paymentMethod,
      isAnnualPrice
    } : {
      creditCardDetail,
      directDebitDetail,
      paymentMethod,
      isAnnualPrice
    };

    history.push(confirmationPath, {
      recaptchaToken,
      paymentProfile
    });
  };
  useEffect(() => {
    if (!hasPurchaseSubmitted && submitting && recaptchaToken) {
      doCheckout();
    }
  }, [hasPurchaseSubmitted, submitting, recaptchaToken]);
  const trackParams = {
    event: GTM_ANALYTICS_CONSTANTS.ADD_PAYMENT_INFO_SEGMENT,
    userId,
    anonymousId,
    properties: {
      currency,
      value: totalPrice,
      coupon: promoCode,
      myobVisitorId
    }
  };
  usePartnerSignupPaymentInfoEventGA4();
  useQuoteAcceptPaymentInfoEventGA4();
  useAddPaymentInfoEventGA4();
  useAddPaymentInfoEvent(GTM_ANALYTICS_CONSTANTS.ADD_PAYMENT_INFO_SEGMENT);
  useSendEventToSegment({
    shouldSendSegmentEvent: !!currency,
    trackParams
  });
  const trackPaymentSubmission = useCallback((isValidPaymentProfile: boolean) => {
    trackEvent(isValidPaymentProfile ? 'payment_profile_valid' : 'payment_profile_invalid', {
      payment_type: paymentMethod
    });
  }, [paymentMethod]);

  if (hasPurchaseSubmitted) {
    return <Redirect to={'/error'}/>;
  }

  const handleSubmit = (isValidPaymentProfile: boolean) => {
    trackPaymentSubmission(isValidPaymentProfile);
    if (isValidPaymentProfile) {
      setSubmitting(true);
    }
  };

  if (isAuth0Loading) {
    return <Spinner/>;
  }

  if (!isAuthenticated) {
    return <Redirect to='/error' />;
  }

  const handlePaymentOptionChange = (paymentMethodOption: PAYMENT_METHOD) => {
    setPaymentMethod(paymentMethodOption);
    setPaymentMethodInContext(paymentMethodOption);
  };
  const renderPaymentForm = () => (paymentMethod === PAYMENT_METHOD.CREDIT ?
    <CreditCardForm
      isPaymentProfileCreationLoading={false}
      setCreditCardDetail={setCreditCardDetail}
      onPaymentProfileSubmit={handleSubmit}
    /> :
    <DirectDebitForm
      isPaymentProfileCreationLoading={false}
      setDirectDebitDetail={setDirectDebitDetail}
      onPaymentProfileSubmit={handleSubmit}
      promoCode={promoCode}
    />);

  return (isStripePaymentFormEnabled ? (
    <StripePaymentForm
      setPaymentMethodId={setPaymentMethodId}
      onPaymentProfileSubmit={handleSubmit}
      onPaymentOptionChange={handlePaymentOptionChange}
      promoCode={promoCode}
      region={region}
      theme={theme}
    />
  ) : (
    <PaymentForm
      paymentMethod={paymentMethod}
      handlePaymentOptionChange={handlePaymentOptionChange}
    >
      {renderPaymentForm()}
    </PaymentForm>
  ));
};
